import React, { ReactElement, useContext, useState, MouseEvent } from 'react';
import classNames from 'classnames';
import { sanitizeString } from '@sm/utils';
import { Box } from '@wds/box';
import { Theme } from '@wds/styles';
import { Typography } from '@wds/typography';
import { useTheme } from 'react-jss';
import { EnterpriseThankYouContext, PageVariants } from '../..';
import useStyles from './useStyles';

type Props = {
  bodyImageUrl: string;
  bodyText: string;
  bodyImageRedirectUrl: string;
};

function EnterpriseThankYouBody({ bodyImageUrl, bodyText, bodyImageRedirectUrl }: Props): ReactElement | null {
  const { pageVariant } = useContext(EnterpriseThankYouContext);
  const premiumCustom = pageVariant === PageVariants.premiumCustom;
  const [imageLoadSuccess, setImageLoadSuccess] = useState(true);
  const theme = useTheme<Theme>();
  const { bodyTextStyles, contentPadding, image } = useStyles({
    isRedirect: Boolean(bodyImageRedirectUrl),
    wrenchTheme: theme,
  });

  const BodyText = (): ReactElement | null => {
    const handleBodyTextClick = (e: MouseEvent<HTMLDivElement>): void => {
      // If the user has clicked on a hyperlink (a tag) then force it to open the link in a new tab
      if ((e.target as HTMLElement).tagName === 'A') {
        e.preventDefault();
        window.open((e.target as HTMLAnchorElement).href, '_blank');
      }
    };
    return bodyText ? (
      <Box className={contentPadding} display="flex" flexJustify="center">
        {/* used <div> instead of Typography to match marketing style */}
        <div
          role="presentation"
          className={classNames(bodyTextStyles)}
          dangerouslySetInnerHTML={{ __html: sanitizeString(bodyText) }}
          onClick={handleBodyTextClick}
        />
      </Box>
    ) : null;
  };

  const BodyImage = (): ReactElement | null => {
    const redirectLoadSuccess = (
      <Box className={contentPadding} display="flex" flexJustify="center">
        <a rel="noopener noreferrer" target="_blank" {...(bodyImageRedirectUrl && { href: bodyImageRedirectUrl })}>
          <img
            data-testid="body__Image"
            className={image}
            src={bodyImageUrl}
            alt=""
            onError={() => setImageLoadSuccess(false)}
          />
        </a>
      </Box>
    );

    const redirectLoadFail =
      bodyImageRedirectUrl && !imageLoadSuccess ? (
        <Box className={contentPadding} display="flex" flexJustify="center">
          <a rel="noopener noreferrer" target="_blank" href={bodyImageRedirectUrl}>
            <Typography className={bodyTextStyles} align="center" color="dark">
              {bodyImageRedirectUrl}
            </Typography>
          </a>
        </Box>
      ) : null;

    return bodyImageUrl && imageLoadSuccess ? redirectLoadSuccess : redirectLoadFail;
  };

  return premiumCustom ? (
    <Box>
      <BodyText />
      <BodyImage />
    </Box>
  ) : null;
}

export default EnterpriseThankYouBody;
